// Shared preview_text renderer. Writers call this inside the builder layer
// so every insert stamps the same preview_text that every reader surface
// displays (channel list, inbox, APNs body, Claude Code relay).
//
// Swift mirror: apps/native/ios/Ano/Core/Models/MessagePreview.swift.
// Keep the per-kind rules in sync with the Swift implementation.

import type { MessageKind } from "./message-payload";

export interface RenderPreviewInput {
  kind: MessageKind;
  content: string;
  // Structured columns extracted by the builder. When rendering at insert time
  // the builder passes these directly; when re-rendering after an edit they
  // come from the existing row.
  calendar_title?: string | null;
  calendar_start_at?: number | null;
  email_subject?: string | null;
  email_from_email?: string | null;
  terminal_command?: string | null;
  terminal_exit_code?: number | null;
  call_duration_seconds?: number | null;
  // payload is consulted for kinds that render preview from payload-only data
  // (link_only, media_group, system).
  payload?: Record<string, unknown> | null;
}

const PREVIEW_MAX = 120;

export function renderPreview(input: RenderPreviewInput): string {
  const { kind } = input;
  switch (kind) {
    case "text":
      return truncate(stripMarkdown(input.content));
    case "image":
      return nonEmptyCaption(input.content) ?? "Sent an image";
    case "video":
      return nonEmptyCaption(input.content) ?? "Sent a video";
    case "audio":
      return nonEmptyCaption(input.content) ?? "Sent audio";
    case "gif":
      return nonEmptyCaption(input.content) ?? "Sent a GIF";
    case "file":
      return nonEmptyCaption(input.content) ?? "Sent a file";
    case "media_group":
      return nonEmptyCaption(input.content) ?? renderMediaGroupPreview(input);
    case "link_only":
      return renderLinkOnlyPreview(input);
    case "email": {
      const subject = (input.email_subject ?? "").trim();
      return subject ? `Email: ${truncate(subject, 100)}` : "Email";
    }
    case "calendar": {
      const title = (input.calendar_title ?? "").trim();
      return title ? `Calendar: ${truncate(title, 100)}` : "Calendar event";
    }
    case "terminal_output":
    case "run": {
      const cmd = (input.terminal_command ?? "").trim();
      const failed =
        typeof input.terminal_exit_code === "number" &&
        input.terminal_exit_code !== 0;
      const base = cmd ? `Ran \`${truncate(cmd, 80)}\`` : "Ran a command";
      return failed ? `${base} (failed)` : base;
    }
    case "terminal_snapshot":
      return "Terminal snapshot";
    case "terminal_live":
      return "Started a live terminal share";
    case "terminal_recording":
      return "Terminal recording";
    case "command_history":
      return "Shared command history";
    case "call_started_video":
    case "call_started_audio":
      return "Started a call";
    case "call_ended": {
      const secs = input.call_duration_seconds;
      if (typeof secs === "number" && secs > 0) {
        return `Call ended · ${formatDuration(secs)}`;
      }
      return "Call ended";
    }
    case "system":
      return renderSystemPreview(input);
    default: {
      // Exhaustiveness guard. If a new kind is added and this switch is not
      // updated, TypeScript reports the missing branch at compile time.
      const _exhaustive: never = kind;
      void _exhaustive;
      return truncate(stripMarkdown(input.content));
    }
  }
}

function nonEmptyCaption(content: string): string | null {
  const trimmed = content.trim();
  return trimmed ? truncate(stripMarkdown(trimmed)) : null;
}

function renderMediaGroupPreview(input: RenderPreviewInput): string {
  const items = Array.isArray(input.payload?.items) ? input.payload?.items : [];
  const count = (items as unknown[]).length;
  if (count > 1) return `Sent ${count} attachments`;
  return "Sent attachments";
}

function renderLinkOnlyPreview(input: RenderPreviewInput): string {
  const caption = nonEmptyCaption(input.content);
  if (caption) return caption;
  const url =
    typeof input.payload?.url === "string" ? (input.payload.url as string) : "";
  return url ? truncate(url, PREVIEW_MAX) : "Shared a link";
}

function renderSystemPreview(input: RenderPreviewInput): string {
  const event =
    typeof input.payload?.event === "string"
      ? (input.payload.event as string)
      : "";
  // Default: show the existing content string. Server writers supply a human
  // sentence there today (e.g. "Alice added Bob to #design"); the event key
  // is the machine-readable form in payload.
  const contentPreview = nonEmptyCaption(input.content);
  if (contentPreview) return contentPreview;
  return event ? `System: ${event}` : "System event";
}

function stripMarkdown(input: string): string {
  // Minimal — matches the Swift implementation. Aims to avoid leaking
  // markdown syntax into channel-list previews or push-notification bodies.
  return input
    .replace(/```[\s\S]*?```/g, "") // fenced code blocks
    .replace(/`([^`]+)`/g, "$1") // inline code
    .replace(/\*\*([^*]+)\*\*/g, "$1") // bold
    .replace(/\*([^*]+)\*/g, "$1") // italic
    .replace(/__([^_]+)__/g, "$1") // alt bold
    .replace(/_([^_]+)_/g, "$1") // alt italic
    .replace(/~~([^~]+)~~/g, "$1") // strikethrough
    .replace(/!\[[^\]]*\]\([^)]+\)/g, "") // images
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // links — keep label
    .replace(/^#{1,6}\s+/gm, "") // headings
    .replace(/^>\s?/gm, "") // blockquotes
    .replace(/\s+/g, " ")
    .trim();
}

function truncate(input: string, max = PREVIEW_MAX): string {
  if (input.length <= max) return input;
  return input.slice(0, max - 1).trimEnd() + "…";
}

function formatDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return `${Math.max(1, Math.round(seconds))}s`;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
}
